import { useEffect, useState } from 'react';

/**
 * React hook for responsive design using media queries / メディアクエリを使用したレスポンシブデザイン用のReactフック
 *
 * This hook listens to window resize events and returns whether the given media query matches.
 * It automatically wraps the query in parentheses and handles media query change events.
 * ウィンドウのリサイズイベントをリッスンし、指定されたメディアクエリがマッチするかどうかを返します。
 * クエリを自動的に括弧で囲み、メディアクエリの変更イベントを処理します。
 *
 * @param {string} query - The media query string (without parentheses) / メディアクエリ文字列（括弧なし）
 * @returns {boolean} True if the media query matches, false otherwise / メディアクエリがマッチする場合はtrue、それ以外はfalse
 *
 * @example
 * // Check if screen width is at least 768px / 画面幅が768px以上かどうかをチェック
 * const isTablet = useMediaQuery('min-width: 768px');
 *
 * @example
 * // Check if device prefers dark mode / デバイスがダークモードを好むかどうかをチェック
 * const prefersDark = useMediaQuery('prefers-color-scheme: dark');
 *
 * @example
 * // Check multiple conditions / 複数の条件をチェック
 * const isLandscapeTablet = useMediaQuery('min-width: 768px) and (orientation: landscape');
 */
export const useMediaQuery = (query: string) => {
  const formattedQuery = `(${query})`;
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia(formattedQuery);

    if (mql.media === 'not all' || mql.media === 'invalid') {
      console.error(`useMediaQuery Error: Invalid media query - ${formattedQuery}`);
      return;
    }

    const updateMatches = (e: MediaQueryListEvent) => setMatches(e.matches);

    setMatches(mql.matches);
    mql.addEventListener('change', updateMatches);

    return () => mql.removeEventListener('change', updateMatches);
  }, [formattedQuery]);

  return matches;
};
